// ============================================================================
// BusinessReportPage — route /app/reports/business  (namespace: "businessReport")
// ----------------------------------------------------------------------------
// The trainer's BUSINESS / INCOME report: sessions delivered, hours (total +
// per client), income (total / paid / outstanding in the trainer's currency),
// month-by-month trends (animated charts), busiest clients, and average fee per
// session + per hour — over a selectable period.
//
// Consumes GET /api/reports/business?period= via the typed API client + React
// Query hook (useBusinessReport). The AppShell's live-sync socket invalidates
// qk.reports.* on any session/measurement/goal/client change, so this refreshes
// across devices with no reload.
//
// Optional-first: every section renders ONLY when it has data, and the INCOME
// section is omitted entirely when no delivered session ever carried a fee.
// Mobile-first (designed at 390), rich Framer-Motion animation, fully
// reduced-motion aware; built entirely on the locked Pulse design system.
//
// Routing + i18n are pre-wired (see app/router.tsx, i18n/config.ts); this file
// stays a DEFAULT export named BusinessReportPage.
// ============================================================================
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBusinessReport } from "../../hooks/reports";
import { EmptyState } from "../../components";
import { LoadingBlock, ErrorBlock } from "../../app/PageState";

import { ReportHeader } from "./ReportHeader";
import { OverviewSection } from "./sections/OverviewSection";
import { ActivitySection } from "./sections/ActivitySection";
import { IncomeSection } from "./sections/IncomeSection";
import { TrendsSection } from "./sections/TrendsSection";
import { hasAnyData, hasIncome, isReportPeriod, parseReport } from "./types";
import type { ReportPeriod } from "./types";

export default function BusinessReportPage() {
  const { t } = useTranslation("businessReport");
  const [period, setPeriod] = useState<ReportPeriod>("all");

  const query = useBusinessReport(period);

  const report = useMemo(
    () => (query.data ? parseReport(query.data) : null),
    [query.data],
  );

  // First load (no cached data yet) → a Pulse loading block.
  if (query.isLoading && !report) {
    return <LoadingBlock label={t("state.loading")} />;
  }

  // Errored with nothing to show → retryable error block.
  if (!report) {
    return <ErrorBlock onRetry={() => void query.refetch()} />;
  }

  const onPeriodChange = (p: string) => {
    if (isReportPeriod(p)) setPeriod(p);
  };

  const anyData = hasAnyData(report);

  // Section index drives the staggered rise-in; bump it as sections appear so
  // the cascade stays even when income is omitted.
  let sectionIndex = 1;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <ReportHeader period={period} onPeriodChange={onPeriodChange} />

      {!anyData ? (
        <EmptyState title={t("empty.title")} description={t("empty.blurb")} />
      ) : (
        <div className="space-y-6">
          <OverviewSection report={report} />
          <ActivitySection report={report} index={sectionIndex++} />
          {hasIncome(report.income) && (
            <IncomeSection income={report.income} index={sectionIndex++} />
          )}
          <TrendsSection report={report} index={sectionIndex++} />
        </div>
      )}

      <footer className="pt-2 text-center text-xs text-body-faint">
        <p>{t("footer.confidential")}</p>
        <p className="mt-1">{t("footer.generatedBy", { app: t("app.name") })}</p>
      </footer>
    </div>
  );
}
